<script lang="ts" setup>
import { CallModel, CallStatus, CallType, Permission } from '@/types';
import { formatDateAsString, formatSecToTime } from '@/utils/datetime';
import { CalendarIcon, ClockIcon, PlayIcon, UserIcon } from '@heroicons/vue/solid';
import { computed, PropType } from 'vue';
import moment from 'moment';
import { ContactSentiment, contactSentimentMap } from '@/utils/contactSentiment';

const props = defineProps({
    call: {
        type: Object as PropType<CallModel>,
        required: true,
    },
    showViewButton: {
        type: Boolean,
        default: true,
    },
});

const duration = computed(() => {
    return moment(props.call?.endedAt).diff(moment(props.call.answeredAt), 'seconds');
});

const sourceEvent = computed(() => (props.call.events?.length ? props.call.events[0] : null));

const destinationEvent = computed(() => {
    const events = props.call.events ?? [];
    if (!events.length) return null;

    if (props.call.type === CallType.INBOUND && props.call.status === CallStatus.ANSWERED) {
        return events.find((event) => event.status === props.call.status) ?? null;
    } else if (props.call.type === CallType.OUTBOUND) {
        return events[0];
    }
    return events[events.length - 1];
});

const sentiment = computed(() => {
    const value = props.call.speechMetrics?.clientSentimentEnd;
    return value && Object.hasOwn(contactSentimentMap, value)
        ? contactSentimentMap[value as ContactSentiment]
        : contactSentimentMap.unidentified;
});
</script>

<template>
    <ol class="mt-5 space-y-3 pl-6">
        <li class="relative scroll-mt-24">
            <span
                class="ring-background absolute -left-[31px] top-5 h-2.5 w-2.5 rounded-full"
                :class="sentiment.bulletTone"
            ></span>
            <UiPanel class="hover:bg-white">
                <div class="flex flex-wrap justify-between">
                    <div class="flex flex-wrap items-center gap-x-4 gap-y-2">
                        <div class="flex items-center space-x-1 text-xs text-gray-600">
                            <CalendarIcon class="h-4 w-4" aria-hidden="true" />
                            <span class="truncate">{{
                                formatDateAsString(call.startedAt.toString(), 'DD/MM/YYYY')
                            }}</span>
                            <span class="truncate">{{
                                formatDateAsString(call.startedAt.toString(), 'HH:mm:ss')
                            }}</span>
                        </div>
                        <div class="mb-2 sm:mb-0">
                            <span
                                class="rounded border-gray-200 bg-gray-50 px-1.5 py-0.5 text-xs ring-1 ring-gray-300"
                            >
                                {{ $t(`call.type.${call.type}`) }}
                            </span>
                        </div>
                        <div class="flex items-center space-x-1 text-sm">
                            <UserIcon class="h-4 w-4" aria-hidden="true" />
                            <span v-if="sourceEvent?.sourceUser" class="truncate">
                                {{ sourceEvent?.sourceUser?.name }}
                            </span>
                            <span v-else-if="destinationEvent?.destinationUser" class="truncate">
                                {{ destinationEvent?.destinationUser?.name }}
                            </span>
                            <span v-else class="truncate">-</span>
                        </div>
                        <div class="flex items-center space-x-1 text-xs text-gray-600">
                            <ClockIcon class="h-4 w-4" aria-hidden="true" />
                            <span>{{ $t('call.list.duration') }}: </span>
                            <span>{{
                                call.status == CallStatus.ANSWERED && call.endedAt
                                    ? formatSecToTime(duration)
                                    : '-'
                            }}</span>
                        </div>
                    </div>
                    <div class="flex items-center lg:justify-end">
                        <UiActionButton
                            v-if="
                                showViewButton &&
                                $can(Permission.VIEW_CALL) &&
                                call.status === 'ANSWERED'
                            "
                            v-tippy="$t('core.actions.View')"
                            variant="primary"
                            :to="{
                                name: 'ViewCallById',
                                params: {
                                    id: call.id,
                                },
                            }"
                        >
                            <PlayIcon class="h-7 w-7" aria-hidden="true" />
                            <span>{{ $t('core.actions.View') }}</span>
                        </UiActionButton>
                    </div>
                </div>
                <div>
                    <p class="mt-2 text-sm text-gray-600">
                        {{ call.summary?.summaryText ?? $t('call.list.noSummary') }}
                    </p>
                </div>
                <div class="mt-3 flex flex-wrap items-center gap-2">
                    <span
                        class="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium"
                        :class="sentiment.tone"
                    >
                        <span v-if="sentiment.face">{{ sentiment.face }}</span>
                        {{ $t(sentiment.label) }}
                    </span>
                </div>
            </UiPanel>
        </li>
    </ol>
</template>
